import { useMemo, useState } from "react";
import { ListPlus, Loader2 } from "lucide-react";
import { Btn, Modal } from "../../../ui";
import { isSiteExcluded } from "../../../../lib/scanExclusions";
import { bulkScan, type BulkScanStarted } from "../../../../lib/siteScan";
import type { T } from "../types";

/**
 * Paste a list of addresses and scan all of them.
 *
 * The counter under the box is not decoration: a pasted column out of a
 * spreadsheet is usually half addresses and half something else, and seeing "18
 * addresses" before pressing the button is what stops an operator waiting for a
 * batch that was never going to include the rows they cared about.
 *
 * The server answers immediately and scans in the background, so this dialog
 * closes on submit rather than pretending to track progress it cannot see. Rows
 * appear at the top of the table as they land.
 */

/** The same splitting rule the API uses, for the live count. */
const SPLIT = /[\s,;]+/;

function countAddresses(input: string): number {
  const seen = new Set<string>();
  for (const raw of input.split(SPLIT)) {
    const token = raw.trim().replace(/^["'<]+|[">']+$/g, "");
    if (!token) continue;
    // Anything with a dot and no spaces is an address as far as counting goes;
    // the server does the real parsing.
    const host = token.replace(/^https?:\/\//i, "").split("/")[0]?.toLowerCase() ?? "";
    if (host.includes(".")) seen.add(host.replace(/^www\./, ""));
  }
  return seen.size;
}

export function BulkScanDialog({
  open,
  onClose,
  onStarted,
  t,
}: {
  open: boolean;
  onClose: () => void;
  onStarted: (started: BulkScanStarted) => void;
  t: T;
}) {
  const [input, setInput] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = useMemo(() => countAddresses(input), [input]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await bulkScan(input, refresh);
      onStarted(result);
      setInput("");
      onClose();
    } catch (err) {
      // Every pasted site is on the do-not-scan list: say so rather than "failed".
      setError(t(isSiteExcluded(err) ? "scanner.bulk.allExcluded" : "scanner.bulk.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    // `width`, not `max-width`: `.crm-modal` sets `width: min(520px, 100%)`, so
    // a wider `max-w-` can only narrow it. A pasted column of addresses wants
    // the room.
    <Modal
      title={
        <span className="flex items-center gap-2">
          <ListPlus className="size-4 text-primary" /> {t("scanner.bulk.title")}
        </span>
      }
      label={t("scanner.bulk.title")}
      onClose={onClose}
      className="w-[min(620px,100%)]"
      footer={
        <>
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="button" tone="brand" disabled={busy || count === 0} onClick={() => void submit()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t(busy ? "scanner.bulk.starting" : "scanner.bulk.start")}
          </Btn>
        </>
      }
    >
      <p className="crm-sub mt-1">{t("scanner.bulk.intro")}</p>

      <textarea
        className="crm-area mt-3 font-mono text-xs"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={9}
        spellCheck={false}
        placeholder={t("scanner.bulk.placeholder")}
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          aria-pressed={refresh}
          onClick={() => setRefresh((v) => !v)}
          className={`crm-cb crm-cb-wide crm-cb-sm${refresh ? " is-on" : ""}`}
        >
          {t("scanner.bulk.refresh")}
        </button>
        <span className="crm-push crm-mut text-xs">{t("scanner.bulk.count", { count })}</span>
      </div>

      {error && <p className="crm-alert mt-3 px-4 py-2.5 text-xs">{error}</p>}
    </Modal>
  );
}
